import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { LoaderBar } from 'components'
import { AppPrimaryButton } from 'elements'
import { DesktopMode, MobileMode } from 'layout'
import { useNotification } from 'hooks/toast'
import { GetTeamMemberList } from 'hooks/user'
import {
    ActionPlanRuleService,
    EscalationPolicyService,
    GroupService,
    PaginationType,
} from 'utility'
import { EscalationPolicyFormModal } from './EscalationPolicyFormModal'
import {
    enrichPoliciesWithRecipientNames,
    formatRecipientChipLabel,
    formatReminderLabel,
    getApiErrorMessage,
    normalizeActionPlanTeamList,
    normalizeEscalationPolicyList,
    normalizeTeamGroupList,
    normalizeVendorCompanyList,
    policyMatchesSearch,
} from './utils'
import { ActiveChip, LinkBtn, TypeChip } from './styles'
import { PencilSimple } from 'phosphor-react'
import {
    PageTitle,
    PageSubtitle,
    ToolbarRow,
    SearchInput,
    TipCard,
    TicketTableWrap,
    TicketTableScroll,
    TicketTable,
    TicketMobileList,
    TicketMobileCard,
    TicketMobileTop,
    TicketMobileTitle,
    TicketMobileFooter,
} from 'page-components/auto-ticket-rules/styles'

const recipientKind = (typeId, typeName) => {
    const n = String(typeName || '').toLowerCase()
    if (n.includes('vendor') || Number(typeId) === 3 || Number(typeId) === 4) {
        return 'vendor'
    }
    if (n.includes('team') || Number(typeId) === 2) return 'team'
    return 'user'
}

export const EscalationPolicyList = () => {
    const { successToast, errorToast } = useNotification()
    const toastRef = useRef({ successToast, errorToast })
    toastRef.current = { successToast, errorToast }
    const { data: teamMembers } = GetTeamMemberList(PaginationType.All)
    const [loading, setLoading] = useState(true)
    const [policies, setPolicies] = useState([])
    const [teams, setTeams] = useState([])
    const [vendors, setVendors] = useState([])
    const [search, setSearch] = useState('')
    const [editing, setEditing] = useState(null)
    const [createOpen, setCreateOpen] = useState(false)

    const load = useCallback(async () => {
        setLoading(true)
        try {
            const [policyRes, teamsRes, groupRes, vendorsRes] =
                await Promise.all([
                    EscalationPolicyService.list(),
                    ActionPlanRuleService.getTeams().catch(() => []),
                    GroupService.list({
                        PageNumber: 1,
                        PageSize: 500,
                    }).catch(() => null),
                    ActionPlanRuleService.getVendorCompanies().catch(() => []),
                ])
            const actionPlanTeams = normalizeActionPlanTeamList(teamsRes)
            const groupTeams =
                groupRes && !actionPlanTeams.length
                    ? normalizeTeamGroupList(groupRes)
                    : []
            setTeams(actionPlanTeams.length ? actionPlanTeams : groupTeams)
            setVendors(normalizeVendorCompanyList(vendorsRes))
            setPolicies(normalizeEscalationPolicyList(policyRes))
        } catch (err) {
            toastRef.current.errorToast(getApiErrorMessage(err))
            setPolicies([])
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        load()
    }, [load])

    const displayPolicies = useMemo(
        () =>
            enrichPoliciesWithRecipientNames(policies, {
                teamMembers,
                teams,
                vendors,
            }),
        [policies, teamMembers, teams, vendors],
    )

    const filtered = useMemo(
        () => displayPolicies.filter(p => policyMatchesSearch(p, search)),
        [displayPolicies, search],
    )

    const handleSaved = saved => {
        // Clear search so the new/updated row is not hidden by a stale filter
        setSearch('')
        setCreateOpen(false)
        setEditing(null)

        if (saved?.softDeleted) {
            successToast(
                'Policy soft-deleted — now Disabled. Rule linkages kept.',
            )
            setPolicies(prev =>
                prev.map(p =>
                    Number(p.id) === Number(saved.id)
                        ? {
                              ...p,
                              ...saved,
                              isActive: false,
                          }
                        : p,
                ),
            )
            return
        }

        successToast('Policy saved')
        // Optimistic insert/update so UI updates before GET returns
        if (saved?.id != null) {
            setPolicies(prev => {
                const exists = prev.some(
                    p => Number(p.id) === Number(saved.id),
                )
                if (exists) {
                    return prev.map(p =>
                        Number(p.id) === Number(saved.id)
                            ? { ...p, ...saved }
                            : p,
                    )
                }
                return [saved, ...prev]
            })
        }
        load()
    }

    if (loading && !policies.length) {
        return <LoaderBar content="Loading escalation policies…" />
    }

    return (
        <div>
            <PageTitle style={{ fontSize: 20, marginBottom: 4 }}>
                Escalation Policies
            </PageTitle>
            <PageSubtitle>
                Reusable escalation rules. Attach one to any auto-ticket rule to
                enable reminders, escalation, and overdue notifications on
                tickets it creates.
            </PageSubtitle>

            <ToolbarRow>
                <SearchInput
                    type="text"
                    placeholder="Search policies…"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                />
                <div className="spacer" />
                <AppPrimaryButton onClick={() => setCreateOpen(true)}>
                    + New Policy
                </AppPrimaryButton>
            </ToolbarRow>

            {filtered.length === 0 ? (
                <TipCard>
                    <h2>
                        {search ? 'No matches' : 'No policies yet'}
                    </h2>
                    <p>
                        {search
                            ? 'No policies match your search. Clear the search box to see all policies.'
                            : 'Create a reusable escalation policy, then attach it on the auto-ticket rule editor.'}
                    </p>
                </TipCard>
            ) : (
                <TicketTableWrap>
                    <DesktopMode>
                        <TicketTableScroll>
                            <TicketTable>
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Reminder</th>
                                        <th>Escalation recipient</th>
                                        <th>Rules using this</th>
                                        <th>Active</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.map(policy => (
                                        <tr key={policy.id}>
                                            <td>
                                                <b>{policy.name}</b>
                                            </td>
                                            <td>
                                                {formatReminderLabel(
                                                    policy.reminderMinutes,
                                                )}
                                            </td>
                                            <td>
                                                {policy.recipientName || '—'}
                                                <TypeChip
                                                    $kind={recipientKind(
                                                        policy.recipientTypeId,
                                                        policy.recipientTypeName,
                                                    )}
                                                >
                                                    {formatRecipientChipLabel(
                                                        policy.recipientTypeId,
                                                        policy.recipientTypeName,
                                                    )}
                                                </TypeChip>
                                            </td>
                                            <td>
                                                {policy.rulesUsingThis
                                                    ? `${policy.rulesUsingThis} rule${
                                                          policy.rulesUsingThis ===
                                                          1
                                                              ? ''
                                                              : 's'
                                                      }`
                                                    : '—'}
                                            </td>
                                            <td>
                                                <ActiveChip
                                                    $active={policy.isActive}
                                                >
                                                    {policy.isActive
                                                        ? 'ACTIVE'
                                                        : 'DISABLED'}
                                                </ActiveChip>
                                            </td>
                                            <td>
                                                <LinkBtn
                                                    type="button"
                                                    onClick={() =>
                                                        setEditing(policy)
                                                    }
                                                >
                                                    Edit
                                                </LinkBtn>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </TicketTable>
                        </TicketTableScroll>
                    </DesktopMode>
                    <MobileMode>
                        <TicketMobileList>
                            {filtered.map(policy => (
                                <TicketMobileCard
                                    key={policy.id}
                                    type="button"
                                    onClick={() => setEditing(policy)}
                                    aria-label={`Edit ${policy.name}`}
                                >
                                    <TicketMobileTop>
                                        <TicketMobileTitle
                                            style={{ marginBottom: 0, flex: 1 }}
                                        >
                                            {policy.name}
                                        </TicketMobileTitle>
                                        <span
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: 4,
                                                color: '#2563eb',
                                                fontSize: 13,
                                                fontWeight: 600,
                                                flexShrink: 0,
                                            }}
                                        >
                                            <PencilSimple size={16} weight="bold" />
                                            Edit
                                        </span>
                                    </TicketMobileTop>
                                    <TicketMobileFooter>
                                        <div>
                                            <span className="label">
                                                Reminder
                                            </span>
                                            <span className="value">
                                                {formatReminderLabel(
                                                    policy.reminderMinutes,
                                                )}
                                            </span>
                                        </div>
                                        <div>
                                            <span className="label">
                                                Recipient
                                            </span>
                                            <span className="value">
                                                {policy.recipientName || '—'}
                                            </span>
                                        </div>
                                        <div>
                                            <span className="label">Status</span>
                                            <span className="value">
                                                {policy.isActive
                                                    ? 'Active'
                                                    : 'Disabled'}
                                            </span>
                                        </div>
                                    </TicketMobileFooter>
                                </TicketMobileCard>
                            ))}
                        </TicketMobileList>
                    </MobileMode>
                </TicketTableWrap>
            )}

            <EscalationPolicyFormModal
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                onSaved={handleSaved}
            />
            <EscalationPolicyFormModal
                open={Boolean(editing)}
                policy={editing}
                onClose={() => setEditing(null)}
                onSaved={handleSaved}
            />
        </div>
    )
}
